/**
 * Complementary Products
 * - Per-card variant selection via clickable value pills (out-of-stock struck through)
 * - Quantity stepper + live price
 * - Adds the selected variant/quantity via /cart/add.js
 * - Re-renders the block(s) on ACTUAL cart change (token + item count), via the
 *   Section Rendering API, preserving any in-progress selection/quantity so a
 *   background cart morph doesn't reset the card mid-interaction.
 */
(function () {
  if (window.__complementaryProductsInit) return;
  window.__complementaryProductsInit = true;

  const SELECTORS = {
    section: '[data-complementary-section]',
    block: '[data-complementary-products]',
    card: '[data-complementary-card]',
    addButton: '[data-complementary-add]',
    valueBtn: '[data-complementary-value]',
    optionGroup: '[data-complementary-option-group]',
    qtyInput: '[data-qty-input]',
  };

  let refreshing = false;
  let lastCartSignature = null;

  /* ----------------------------- Money ----------------------------- */

  function formatMoney(cents, format) {
    const fmt = format || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const [intPart, decPart] = value.split('.');
    const whole = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const withCents = whole + '.' + decPart;
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, withCents)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, whole)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withCents.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, whole);
  }

  /* ----------------------- Variant resolution ----------------------- */

  function getVariants(card) {
    const script = card.querySelector('[data-complementary-variants]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch {
      return [];
    }
  }

  // Currently selected value for each option position (1-indexed -> value).
  function selectedValues(card) {
    const map = {};
    card.querySelectorAll('[data-complementary-value].is-selected').forEach((btn) => {
      map[Number(btn.dataset.optionPosition)] = btn.dataset.value;
    });
    return map;
  }

  function selectedOptionArray(card) {
    const map = selectedValues(card);
    const positions = Object.keys(map).map(Number).sort((a, b) => a - b);
    return positions.map((p) => map[p]);
  }

  function matchVariant(card) {
    const variants = getVariants(card);
    if (variants.length === 1) return variants[0];
    const chosen = selectedOptionArray(card);
    if (chosen.length !== variants[0]?.options.length) return null;
    return variants.find((v) => v.options.every((opt, i) => opt === chosen[i])) || null;
  }

  // Recompute strikethrough for each value, given the other selected options.
  function refreshAvailability(card) {
    const variants = getVariants(card);
    if (variants.length <= 1) return;
    const chosen = selectedValues(card);

    card.querySelectorAll('[data-complementary-value]').forEach((btn) => {
      const pos = Number(btn.dataset.optionPosition);
      const val = btn.dataset.value;
      // A value is available if some available variant has this value at this
      // position AND matches all OTHER currently selected option values.
      const available = variants.some((v) => {
        if (!v.available) return false;
        if (v.options[pos - 1] !== val) return false;
        return Object.keys(chosen).every((otherPosStr) => {
          const otherPos = Number(otherPosStr);
          if (otherPos === pos) return true;
          return v.options[otherPos - 1] === chosen[otherPos];
        });
      });
      btn.classList.toggle('is-unavailable', !available);
    });
  }

  function updateCard(card) {
    const block = card.closest(SELECTORS.block);
    const moneyFormat = block ? block.dataset.moneyFormat : null;
    refreshAvailability(card);

    const variant = matchVariant(card);
    const priceEl = card.querySelector('[data-complementary-price]');
    const button = card.querySelector(SELECTORS.addButton);
    const label = button ? button.querySelector('[data-add-label]') : null;

    if (!variant) {
      if (button) button.disabled = true;
      if (label) label.textContent = 'Unavailable';
      return;
    }
    if (priceEl && moneyFormat) priceEl.textContent = formatMoney(variant.price, moneyFormat);
    if (button) {
      button.dataset.variantId = variant.id;
      button.disabled = !variant.available;
    }
    if (label) label.textContent = variant.available ? 'Add to cart' : 'Sold out';
  }

  /* --------------------------- State capture --------------------------- */
  // So a background re-render doesn't wipe what the customer is mid-choosing.

  function captureState() {
    const state = {};
    document.querySelectorAll(SELECTORS.card).forEach((card) => {
      const handle = card.dataset.productHandle;
      if (!handle) return;
      const qtyInput = card.querySelector(SELECTORS.qtyInput);
      state[handle] = {
        selected: selectedValues(card),
        qty: qtyInput ? qtyInput.value : '1',
      };
    });
    return state;
  }

  function restoreState(state) {
    if (!state) return;
    document.querySelectorAll(SELECTORS.card).forEach((card) => {
      const handle = card.dataset.productHandle;
      const saved = state[handle];
      if (!saved) {
        updateCard(card);
        return;
      }
      // Restore selected option pills
      Object.entries(saved.selected).forEach(([pos, val]) => {
        card.querySelectorAll(`[data-complementary-value][data-option-position="${pos}"]`).forEach((btn) => {
          const isMatch = btn.dataset.value === val;
          btn.classList.toggle('is-selected', isMatch);
          btn.setAttribute('aria-pressed', isMatch ? 'true' : 'false');
        });
      });
      // Restore quantity
      const qtyInput = card.querySelector(SELECTORS.qtyInput);
      if (qtyInput && saved.qty) qtyInput.value = saved.qty;
      updateCard(card);
    });
  }

  /* --------------------------- Interactions --------------------------- */

  document.addEventListener('click', (event) => {
    // Variant value selection
    const valueBtn = event.target.closest(SELECTORS.valueBtn);
    if (valueBtn) {
      const card = valueBtn.closest(SELECTORS.card);
      const pos = valueBtn.dataset.optionPosition;
      // Single-select within this option group
      card.querySelectorAll(`[data-complementary-value][data-option-position="${pos}"]`).forEach((b) => {
        const on = b === valueBtn;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      updateCard(card);
      return;
    }

    // Quantity steppers
    const minus = event.target.closest('[data-qty-minus]');
    const plus = event.target.closest('[data-qty-plus]');
    if (minus || plus) {
      const card = event.target.closest(SELECTORS.card);
      const input = card && card.querySelector(SELECTORS.qtyInput);
      if (input) {
        const current = parseInt(input.value, 10) || 1;
        input.value = Math.max(1, current + (plus ? 1 : -1));
      }
      return;
    }
  });

  /* ----------------------------- Add to cart ----------------------------- */

  document.addEventListener('click', async (event) => {
    const button = event.target.closest(SELECTORS.addButton);
    if (!button || button.disabled) return;

    event.preventDefault();
    const card = button.closest(SELECTORS.card);
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const qtyInput = card && card.querySelector(SELECTORS.qtyInput);
    const quantity = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;

    const label = button.querySelector('[data-add-label]');
    const originalText = label ? label.textContent : '';
    button.disabled = true;
    if (label) label.textContent = '…';

    try {
      const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity }] }),
      });
      if (!res.ok) throw new Error('Add to cart failed');

      // Tell Horizon's cart UI to refresh. Horizon's cart-drawer-component
      // listens for a real CartUpdateEvent instance from its @theme/events
      // module — a plain CustomEvent named 'cart:update' is NOT recognised.
      await notifyCartUpdated();

      await refreshComplementary(true);
    } catch (err) {
      console.error('[complementary-products]', err);
      if (label) label.textContent = originalText;
      button.disabled = false;
    }
  });

  /* ----------------- Notify Horizon's cart UI of a change ----------------- */

  async function notifyCartUpdated() {
    let cart = null;
    try {
      const res = await fetch(`${window.Shopify.routes.root}cart.js`, { headers: { Accept: 'application/json' } });
      if (res.ok) cart = await res.json();
    } catch { /* non-fatal */ }

    // 1) Preferred: dispatch Horizon's native CartUpdateEvent.
    try {
      const mod = await import('@theme/events');
      if (mod && mod.CartUpdateEvent) {
        const event = new mod.CartUpdateEvent(cart || {}, 'complementary-products', {
          itemCount: cart ? cart.item_count : undefined,
          source: 'complementary-products',
          sections: {},
        });
        document.dispatchEvent(event);
        return;
      }
    } catch {
      // Module not available on this Horizon version — fall through.
    }

    // 2) Fallback: re-render the cart drawer section ourselves and morph it in.
    try {
      const drawer = document.querySelector('cart-drawer-component, #cart-drawer, .cart-drawer');
      if (drawer) {
        const sectionId = drawer.dataset.sectionId || 'cart-drawer';
        const r = await fetch(`${window.Shopify.routes.root}?section_id=${sectionId}`);
        if (r.ok) {
          const html = await r.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('cart-drawer-component, #cart-drawer, .cart-drawer') || doc.body.firstElementChild;
          if (fresh) drawer.innerHTML = fresh.innerHTML;
        }
      }
    } catch (err) {
      console.error('[complementary-products] cart UI refresh fallback failed', err);
    }

    // 3) Also fire the legacy plain events for any other listeners.
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { source: 'complementary-products' } }));
  }

  /* ----------------------- Re-render on cart change ----------------------- */

  // Build a signature of the cart so we only re-render when contents truly change.
  async function getCartSignature() {
    try {
      const res = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return null;
      const cart = await res.json();
      return cart.item_count + ':' + cart.items.map((i) => i.id + 'x' + i.quantity).join('|');
    } catch {
      return null;
    }
  }

  async function refreshComplementary(force) {
    if (refreshing) return;

    // Skip unless the cart actually changed (unless forced by our own add).
    if (!force) {
      const sig = await getCartSignature();
      if (sig !== null && sig === lastCartSignature) return;
      lastCartSignature = sig;
    }

    refreshing = true;
    const saved = captureState();
    try {
      const sections = document.querySelectorAll(SELECTORS.section);
      const sectionIds = [...new Set([...sections].map((el) => el.dataset.sectionId).filter(Boolean))];
      if (!sectionIds.length) return;

      const url = `${window.Shopify.routes.root}?sections=${sectionIds.join(',')}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('Section render failed');
      const data = await res.json();

      sections.forEach((el) => {
        const id = el.dataset.sectionId;
        if (data[id] == null) return;
        const wrapper = document.createElement('div');
        wrapper.innerHTML = data[id];
        const fresh = wrapper.querySelector(SELECTORS.section);
        el.innerHTML = fresh ? fresh.innerHTML : '';
      });

      restoreState(saved);
      if (force) lastCartSignature = await getCartSignature();
    } catch (err) {
      console.error('[complementary-products]', err);
    } finally {
      refreshing = false;
    }
  }

  /* ------------------- Listen for Horizon cart events ------------------- */

  let debounceTimer;
  const debouncedRefresh = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => refreshComplementary(false), 250);
  };

  ['cart:update', 'cart:refresh', 'cart:change', 'on:cart:update', 'cart:rerender'].forEach((evtName) => {
    document.addEventListener(evtName, (e) => {
      if (e.detail && e.detail.source === 'complementary-products') return;
      debouncedRefresh();
    });
  });

  // No blind subtree MutationObserver — it fired on cosmetic morphs and reset
  // in-progress selections. Cart events + signature check cover real changes.

  /* ----------------------------- Init ----------------------------- */
  document.querySelectorAll(SELECTORS.card).forEach(updateCard);
  getCartSignature().then((sig) => { lastCartSignature = sig; });
})();
